import React, { useState } from 'react';

/**
 * Build the default layer order from formData if none is saved.
 * Order (bottom to top): face, collage_photos, overlay, text fields, mascot
 */
export const buildDefaultLayerOrder = (formData) => {
  const layers = [];

  // Face placement (bottom-most interactive element)
  if (formData.eyes_y != null || formData.face_height != null) {
    layers.push({ type: 'face' });
  }

  // Collage photos — also used by gif_slide, which shares collage's placement UI
  if (formData.look_type === 'collage' || formData.look_type === 'gif_slide') {
    (formData.collage_photos || []).forEach((_, i) => {
      layers.push({ type: 'collage_photo', index: i });
    });
  }

  // Overlay
  if (formData.overlay_s3_path || formData.overlay_filename) {
    layers.push({ type: 'overlay' });
  }

  // Text fields
  (formData.text_fields || []).forEach((_, i) => {
    layers.push({ type: 'text', index: i });
  });

  // Mascot
  if (formData.mascot_center_x != null) {
    layers.push({ type: 'mascot' });
  }

  return layers;
};

/**
 * Ensure layer_order is in sync with current elements.
 * Adds missing elements and removes stale ones.
 */
export const syncLayerOrder = (layerOrder, formData) => {
  const expected = buildDefaultLayerOrder(formData);
  if (!layerOrder || layerOrder.length === 0) return expected;

  // Build a set of current expected layer keys
  const key = (l) => l.index != null ? `${l.type}:${l.index}` : l.type;
  const expectedKeys = new Set(expected.map(key));
  const existingKeys = new Set(layerOrder.map(key));

  // Keep existing order but remove stale entries
  const synced = layerOrder.filter(l => expectedKeys.has(key(l)));

  // Add any new elements not in the saved order (at the top)
  expected.forEach(l => {
    if (!existingKeys.has(key(l))) {
      synced.push(l);
    }
  });

  return synced;
};

const LAYER_LABELS = {
  face: 'Face Placement',
  overlay: 'Overlay',
  mascot: 'Mascot',
  collage_photo: 'Photo',
  text: 'Text Field',
};

const LAYER_COLORS = {
  face: '#e91e63',
  overlay: '#9c27b0',
  mascot: '#4caf50',
  collage_photo: '#1976d2',
  text: '#9c27b0',
};

const getLayerLabel = (layer, formData) => {
  if (layer.type === 'collage_photo') {
    const photo = (formData.collage_photos || [])[layer.index];
    return photo?.look_ref || `Photo ${(layer.index || 0) + 1}`;
  }
  if (layer.type === 'text') {
    const tf = (formData.text_fields || [])[layer.index];
    return tf?.content ? `Text: ${tf.content.substring(0, 20)}` : `Text Field ${(layer.index || 0) + 1}`;
  }
  return LAYER_LABELS[layer.type] || layer.type;
};

const LayerPanel = ({ formData, onChange, selectedElement }) => {
  const layers = syncLayerOrder(formData.layer_order, formData);
  const [dragFrom, setDragFrom] = useState(null);

  const selectedKey = selectedElement
    ? (selectedElement.index != null ? `${selectedElement.type}:${selectedElement.index}` : selectedElement.type)
    : null;

  const moveLayer = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const next = [...layers];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    onChange({ layer_order: next });
  };

  const bringToFront = (idx) => moveLayer(idx, layers.length - 1);
  const sendToBack = (idx) => moveLayer(idx, 0);
  const bringForward = (idx) => { if (idx < layers.length - 1) moveLayer(idx, idx + 1); };
  const sendBackward = (idx) => { if (idx > 0) moveLayer(idx, idx - 1); };

  const handleDragStart = (e, idx) => {
    e.dataTransfer.effectAllowed = 'move';
    setDragFrom(idx);
  };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop = (e, toIdx) => {
    e.preventDefault();
    if (dragFrom !== null && dragFrom !== toIdx) moveLayer(dragFrom, toIdx);
    setDragFrom(null);
  };

  // Render top-to-bottom (reversed from render order)
  const reversed = [...layers].reverse();

  return (
    <div>
      <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#333' }}>Layers</h4>
      <p style={{ fontSize: 10, color: '#999', margin: '0 0 8px' }}>
        Drag to reorder. Top = front, bottom = back.
      </p>

      {reversed.length === 0 && (
        <div style={{ padding: 12, textAlign: 'center', color: '#bbb', fontSize: 12 }}>
          No elements on canvas
        </div>
      )}

      {reversed.map((layer, vi) => {
        const realIdx = layers.length - 1 - vi; // index in the actual layer_order array
        const key = layer.index != null ? `${layer.type}:${layer.index}` : layer.type;
        const isSelected = key === selectedKey;
        const color = LAYER_COLORS[layer.type] || '#666';

        return (
          <div
            key={key}
            draggable
            onDragStart={e => handleDragStart(e, realIdx)}
            onDragOver={handleDragOver}
            onDrop={e => handleDrop(e, realIdx)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '5px 8px', marginBottom: 2,
              border: `1px solid ${isSelected ? color : '#e0e0e0'}`,
              borderRadius: 4,
              background: isSelected ? color + '10' : '#fff',
              fontSize: 12, cursor: 'grab'
            }}
          >
            <span style={{ color: '#bbb', fontSize: 12, userSelect: 'none' }}>⋮⋮</span>
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: color, flexShrink: 0
            }} />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#333' }}>
              {getLayerLabel(layer, formData)}
            </span>

            {isSelected && (
              <div style={{ display: 'flex', gap: 2 }}>
                <button
                  onClick={(e) => { e.stopPropagation(); bringToFront(realIdx); }}
                  title="Bring to Front"
                  disabled={realIdx === layers.length - 1}
                  style={btnStyle(realIdx === layers.length - 1)}
                >⇤</button>
                <button
                  onClick={(e) => { e.stopPropagation(); bringForward(realIdx); }}
                  title="Bring Forward"
                  disabled={realIdx === layers.length - 1}
                  style={btnStyle(realIdx === layers.length - 1)}
                >↑</button>
                <button
                  onClick={(e) => { e.stopPropagation(); sendBackward(realIdx); }}
                  title="Send Backward"
                  disabled={realIdx === 0}
                  style={btnStyle(realIdx === 0)}
                >↓</button>
                <button
                  onClick={(e) => { e.stopPropagation(); sendToBack(realIdx); }}
                  title="Send to Back"
                  disabled={realIdx === 0}
                  style={btnStyle(realIdx === 0)}
                >⇥</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const btnStyle = (disabled) => ({
  width: 22, height: 22, padding: 0,
  border: '1px solid #ccc', borderRadius: 3,
  background: disabled ? '#f5f5f5' : '#fff',
  color: disabled ? '#ccc' : '#333',
  cursor: disabled ? 'default' : 'pointer',
  fontSize: 12, lineHeight: 1,
  display: 'flex', alignItems: 'center', justifyContent: 'center'
});

export default LayerPanel;
